import {
    Cell,
    Column,
    Row,
} from '../../coaching-observation/experimental.grid';
import { createAction } from '../../store.model';
import {
    CoverageDataDto,
    RosterType,
} from '../coverage.coaching-insight.model';
import { ScheduleEventDto } from '../schedule.coaching-insight.model';
import {
    EventParticipant,
    EventType,
} from '../schedule/models/schedule-event.model';

import {
    CellContext,
    ColumnContext,
    RosterItem,
    RowContext,
} from './coaching-coverage-grid.model';
import { CoachingCoverageContext } from './coaching-coverage.model';
import { DateRange } from './select-period-date-range.component';

export enum ActionType {
    COACHING_COVERAGE__ROOT__RELOAD = 'COACHING_COVERAGE__ROOT__RELOAD',
    COACHING_COVERAGE__ROOT__RELOAD_RESULT = 'COACHING_COVERAGE__ROOT__RELOAD_RESULT',
    COACHING_COVERAGE__ROOT__CHANGE_ROSTER_TYPE = 'COACHING_COVERAGE__ROOT__CHANGE_ROSTER_TYPE',
    COACHING_COVERAGE__ROOT__TOGGLE_CREATE_EVENT = 'COACHING_COVERAGE__ROOT__TOGGLE_CREATE_EVENT',
    COACHING_COVERAGE__ROOT__TOGGLE_PERIOD_RANGE_SELECT = 'COACHING_COVERAGE__ROOT__TOGGLE_PERIOD_RANGE_SELECT',
    COACHING_COVERAGE__ROOT__SET_PERIOD_RANGE = 'COACHING_COVERAGE__ROOT__SET_PERIOD_RANGE',
    COACHING_COVERAGE__ROOT__SHIFT_PERIOD_RANGE = 'COACHING_COVERAGE__ROOT__SHIFT_PERIOD_RANGE',
    COACHING_COVERAGE__GRID__TOGGLE_SCHEDULE_FOR_USER = 'COACHING_COVERAGE__GRID__TOGGLE_SCHEDULE_FOR_USER',
    COACHING_COVERAGE__GRID__SORT = 'COACHING_COVERAGE__GRID__SORT',
    COACHING_COVERAGE__GRID__TOGGLE_PERIOD_COLUMN = 'COACHING_COVERAGE__GRID__TOGGLE_PERIOD_COLUMN',
    COACHING_COVERAGE__GRID__TOGGLE_ROW_EXPAND = 'COACHING_COVERAGE__GRID__TOGGLE_ROW_EXPAND',
    COACHING_COVERAGE__GRID__TOGGLE_PERIOD_DATE_RANGE_SELECT = 'COACHING_COVERAGE__GRID__TOGGLE_PERIOD_DATE_RANGE_SELECT',
    COACHING_COVERAGE__GRID__SHIFT_PERIOD_DATE_RANGE = 'COACHING_COVERAGE__GRID__SHIFT_PERIOD_DATE_RANGE',
    COACHING_COVERAGE__GRID__TOGGLE_SHOW_SCHEDULE_ITEMS_LIST = 'COACHING_COVERAGE__GRID__TOGGLE_SHOW_SCHEDULE_ITEMS_LIST',
}

export interface RootReloadAction {
    context: CoachingCoverageContext;
    rosterType?: RosterType;
}

export const rootReload = createAction<ActionType, RootReloadAction>(
    ActionType.COACHING_COVERAGE__ROOT__RELOAD,
);

export interface RootReloadResultAction extends RootReloadAction {
    start?: Date;
    end?: Date;
    coverage?: CoverageDataDto;
    scheduleEvents?: ScheduleEventDto[];
    roster?: RosterItem[];
}

export const rootReloadResult = createAction<
    ActionType,
    RootReloadResultAction
>(ActionType.COACHING_COVERAGE__ROOT__RELOAD_RESULT);

/// //////////////////////

export interface RootChangeRosterType {
    context: CoachingCoverageContext;
    rosterType: RosterType;
}

/** shouldn't be called if state.data was never loaded. call rootReload instead */
export const rootChangeRosterType = createAction<
    ActionType,
    RootChangeRosterType
>(ActionType.COACHING_COVERAGE__ROOT__CHANGE_ROSTER_TYPE);

/// //////////////////////

export interface RootToggleCreateEvent {
    userId: number;
}

export const rootToggleCreateEvent = createAction<
    ActionType,
    RootToggleCreateEvent
>(ActionType.COACHING_COVERAGE__ROOT__TOGGLE_CREATE_EVENT);

/// //////////////////////

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface RootTogglePeriodRangeSelect {}

export const rootTogglePeriodRangeSelect = createAction<
    ActionType,
    RootTogglePeriodRangeSelect
>(ActionType.COACHING_COVERAGE__ROOT__TOGGLE_PERIOD_RANGE_SELECT);

/// //////////////////////

export interface RootSetPeriodRange {
    context: CoachingCoverageContext;
    maxShown: number;
}

export const rootSetPeriodRange = createAction<ActionType, RootSetPeriodRange>(
    ActionType.COACHING_COVERAGE__ROOT__SET_PERIOD_RANGE,
);

/// //////////////////////

export interface RootShiftPeriodRange {
    context: CoachingCoverageContext;
    forward: boolean;
}

export const rootShiftPeriodRange = createAction<
    ActionType,
    RootShiftPeriodRange
>(ActionType.COACHING_COVERAGE__ROOT__SHIFT_PERIOD_RANGE);

/// //////////////////////

export interface GridToggleScheduleForUser {
    managerId: number;
    userId: number;
    user: EventParticipant;
}

export const gridToggleScheduleForUser = createAction<
    ActionType,
    GridToggleScheduleForUser
>(ActionType.COACHING_COVERAGE__GRID__TOGGLE_SCHEDULE_FOR_USER);

/// //////////////////////

export interface GridSortAction {
    column: Column<ColumnContext>;
}

export const gridSort = createAction<ActionType, GridSortAction>(
    ActionType.COACHING_COVERAGE__GRID__SORT,
);

/// //////////////////////

export interface GridTogglePeriodColumnAction {
    column?: Column<ColumnContext>;
}

export const gridTogglePeriodColumn = createAction<
    ActionType,
    GridTogglePeriodColumnAction
>(ActionType.COACHING_COVERAGE__GRID__TOGGLE_PERIOD_COLUMN);

/// //////////////////////

export interface GridToggleRowExpandAction {
    row: Row<RowContext, CellContext>;
}

export const gridToggleRowExpand = createAction<
    ActionType,
    GridToggleRowExpandAction
>(ActionType.COACHING_COVERAGE__GRID__TOGGLE_ROW_EXPAND);

/// //////////////////////

export interface GridTogglePeriodDateRangeSelectAction {
    column: Column<ColumnContext>;
    elementSelector: string;
}

export const gridTogglePeriodDateRangeSelect = createAction<
    ActionType,
    GridTogglePeriodDateRangeSelectAction
>(ActionType.COACHING_COVERAGE__GRID__TOGGLE_PERIOD_DATE_RANGE_SELECT);

/// //////////////////////

export interface GridShiftPeriodDateRangeAction {
    context: CoachingCoverageContext;
    column: Column<ColumnContext>;
    dateRange: DateRange;
}

export const gridShiftPeriodDateRange = createAction<
    ActionType,
    GridShiftPeriodDateRangeAction
>(ActionType.COACHING_COVERAGE__GRID__SHIFT_PERIOD_DATE_RANGE);

/// //////////////////////

export interface GridToggleShowScheduleItemsListAction {
    cell: Cell<CellContext>;
    eventType: EventType;
    elementSelector: string;
}

export const gridToggleShowScheduleItemsList = createAction<
    ActionType,
    GridToggleShowScheduleItemsListAction
>(ActionType.COACHING_COVERAGE__GRID__TOGGLE_SHOW_SCHEDULE_ITEMS_LIST);
